import React from 'react';
import { Card, Row, Col, Button } from 'react-bootstrap';
import { FaArrowLeft, FaShieldAlt, FaExclamationTriangle, FaBuilding } from 'react-icons/fa';
import SecurityTrends from '../graph/SecurityTrends';

const SecurityOrgView = ({ orgId, orgData, accountBreakdown, trendsData, onBack, onAccountSelect }) => {
  const organization = orgData;
  const isStandaloneAccount = orgId === 'standalone';
  const orgAccounts = accountBreakdown?.filter(account => {
    const accountId = account.accountId;
    if (isStandaloneAccount) {
      // For standalone, show all accounts not in any organization
      return true; // Will be filtered by parent component
    }
    const isManagement = organization?.id === accountId;
    const isMember = organization?.memberAccounts?.some(m => m.accountId === accountId);
    return isManagement || isMember;
  }) || [];

  const orgTotals = orgAccounts.reduce((totals, account) => ({
    critical: totals.critical + (account.critical || 0),
    high: totals.high + (account.high || 0),
    medium: totals.medium + (account.medium || 0),
    low: totals.low + (account.low || 0),
    total: totals.total + (account.totalFindings || 0)
  }), { critical: 0, high: 0, medium: 0, low: 0, total: 0 });

  return (
    <div className="container">
      <div className="d-flex align-items-center mb-4">
        <Button variant="outline-secondary" onClick={onBack} className="me-3">
          <FaArrowLeft /> Back to Overview
        </Button>
        <div>
          <h4 style={{ color: '#1e293b', fontWeight: '600', marginBottom: '4px' }}>
            <FaBuilding className="me-2" />
            {isStandaloneAccount ? (organization?.alias || 'Standalone Accounts') : (organization?.alias || 'AWS Organization')}
          </h4>
          <p style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: 0 }}>
            {isStandaloneAccount ? `${orgAccounts.length} ${orgAccounts.length === 1 ? 'account' : 'accounts'}` : `Organization ID: ${orgId} • ${orgAccounts.length} accounts`}
          </p>
        </div>
      </div>

      {/* Organization Summary */}
      <Row className="mb-4">
        <Col md={3}>
          <Card style={{ background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)', color: 'white', borderRadius: '12px', border: 'none' }}>
            <Card.Body className="text-center">
              <div style={{ fontSize: '2rem', marginBottom: '8px' }}><FaExclamationTriangle /></div>
              <h3 className="mb-0">{orgTotals.critical}</h3>
              <small style={{ opacity: 0.9 }}>Critical</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card style={{ background: 'linear-gradient(135deg, #fd7e14 0%, #e8590c 100%)', color: 'white', borderRadius: '12px', border: 'none' }}>
            <Card.Body className="text-center">
              <div style={{ fontSize: '2rem', marginBottom: '8px' }}><FaShieldAlt /></div>
              <h3 className="mb-0">{orgTotals.high}</h3>
              <small style={{ opacity: 0.9 }}>High</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card style={{ background: 'linear-gradient(135deg, #6D5BFF 0%, #5a4ad6 100%)', color: 'white', borderRadius: '12px', border: 'none' }}>
            <Card.Body className="text-center">
              <div style={{ fontSize: '2rem', marginBottom: '8px' }}><FaShieldAlt /></div>
              <h3 className="mb-0">{orgTotals.total}</h3>
              <small style={{ opacity: 0.9 }}>Total Findings</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card style={{ background: 'linear-gradient(135deg, #28a745 0%, #1e7e34 100%)', color: 'white', borderRadius: '12px', border: 'none' }}>
            <Card.Body className="text-center">
              <div style={{ fontSize: '2rem', marginBottom: '8px' }}><FaBuilding /></div>
              <h3 className="mb-0">{orgAccounts.length}</h3>
              <small style={{ opacity: 0.9 }}>Accounts</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Security Trends */}
      {trendsData && trendsData.length > 0 && (
        <Row className="mb-4">
          <Col>
            <SecurityTrends trendsData={trendsData} />
          </Col>
        </Row>
      )}

      {/* Account List */}
      <div className="mb-3">
        <h5 style={{ color: '#1e293b', fontWeight: '600' }}>{isStandaloneAccount ? 'Account Details' : 'Accounts in Organization'}</h5>
        <p style={{ fontSize: '0.875rem', color: '#64748b' }}>Click on an account to view detailed security findings</p>
      </div>

      <Row>
        {orgAccounts.map((account) => {
          const accountInfo = organization?.memberAccounts?.find(m => m.accountId === account.accountId) || 
                             (organization?.id === account.accountId ? { accountId: account.accountId, accountName: 'Management Account' } : null);
          
          return (
            <Col md={6} key={account.accountId} className="mb-3">
              <Card 
                onClick={() => onAccountSelect(account.accountId)}
                style={{ 
                  borderRadius: '12px', 
                  border: '1px solid #e2e8f0', 
                  cursor: 'pointer', 
                  transition: 'all 0.2s', 
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)' 
                }}
                className="h-100"
              >
                <Card.Body>
                  <div className="mb-3">
                    <h6 className="mb-1" style={{ color: '#1e293b', fontWeight: '600' }}>
                      {accountInfo?.accountName || account.accountId}
                    </h6>
                    <small style={{ color: '#64748b' }}>
                      {account.accountId} • Last scan: {account.scanDate}
                    </small>
                  </div>
                  
                  <Row>
                    <Col xs={6}>
                      <div className="text-center">
                        <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#dc3545' }}>
                          {account.critical || 0}
                        </div>
                        <small style={{ color: '#64748b' }}>Critical</small>
                      </div>
                    </Col>
                    <Col xs={6}>
                      <div className="text-center">
                        <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#fd7e14' }}>
                          {account.high || 0}
                        </div>
                        <small style={{ color: '#64748b' }}>High</small>
                      </div>
                    </Col>
                  </Row>
                  
                  <hr style={{ margin: '12px 0' }} />
                  
                  <div className="d-flex justify-content-between">
                    <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Total Findings</span>
                    <strong style={{ color: '#1e293b' }}>{account.totalFindings || 0}</strong>
                  </div>
                  
                  <div className="text-center mt-2" style={{ fontSize: '0.875rem', color: '#6D5BFF' }}>
                    Click to view details →
                  </div>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

export default SecurityOrgView;